import "src/style/style.scss";

import { wrapPageElement } from "./src/gatsby-config/wrapPageElement";
import { wrapRootElement } from "./src/gatsby-config/wrapRootElement";
import { getCurrentGeolocation } from "./src/utils/localization/geolocation/geolocationLoader";
import { GatsbyBrowser } from "gatsby";
import { setScrollOffset, scrollToSection } from "./src/utils/inPageNavigation";
import { assignAbTestSegmentToUser } from "./src/ab-test-experiments/client/client-side-handler";
import { createRoot, hydrateRoot } from "react-dom/client";
import { ErrorInfo, ReactNode } from "react";
import { activateLocale } from "./src/utils/localization/languages/I18nContext";
import {
  languages,
  defaultLanguage
} from "./src/utils/localization/languages/languages";
import { LanguageCode } from "./src/utils/localization/languages/types";
import { envVariableTrue } from "./src/gatsby-config/hosting-config";
import { reportError } from "./src/gatsby/plugins/gatsby-plugin-rollbar/lazyRollbar";

// Set GATSBY_DEBUG_HYDRATION=true to log React 19 hydration mismatches with componentStack
const HYDRATION_DEBUG = envVariableTrue(process.env.GATSBY_DEBUG_HYDRATION);

// Language of the current page. The page's resources are already loaded when
// the hydrate function runs, so the loader returns them synchronously — this
// is the same pageContext.languageCode that wrapPageElement uses (works for
// root-level landing pages too, where the URL carries no language). Falls
// back to <html lang> and then to the default language.
const detectLanguageCode = (): LanguageCode => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const fromContext = w.___loader?.loadPageSync?.(w.pagePath)?.json
      ?.pageContext?.languageCode;
    const fromHtml = document.documentElement.lang?.toLowerCase();
    const candidate = fromContext ?? fromHtml;
    if (languages.some((l) => l.code === candidate)) {
      return candidate as LanguageCode;
    }
  } catch {
    // fall through to default
  }
  return defaultLanguage.code as LanguageCode;
};

// Hydration is gated on the page's own locale catalog (a ~10 KiB gz async
// chunk; the other locales never reach the browser). The SSR HTML is already
// translated, so nothing untranslated can flash: the client simply keeps
// showing it until the catalog is ready and then hydrates with identical text.
export const replaceHydrateFunction: GatsbyBrowser["replaceHydrateFunction"] =
  () => (element: ReactNode, container: Document | Element) => {
    // React's default handler only reports the bare error (via the global
    // reportError); sending it ourselves keeps the componentStack, which is
    // what tells a hydration mismatch's component apart in Rollbar.
    const options = {
      onRecoverableError: (error: unknown, errorInfo: ErrorInfo) => {
        if (HYDRATION_DEBUG)
          console.error(
            "[hydration] recoverable:",
            error,
            "\ncomponentStack:",
            errorInfo?.componentStack
          );
        const message = error instanceof Error ? error.message : String(error);
        reportError(message, error, {
          componentStack: errorInfo?.componentStack
        });
      }
    };
    // Same rule as Gatsby's default renderer: hydrate only when the server
    // sent markup. An empty #___gatsby (dev client-only shell, ?skip-ssr)
    // must be rendered from scratch or React reports a hydration mismatch.
    const hydrate = () =>
      container.children.length
        ? hydrateRoot(container, element, options)
        : createRoot(container, options).render(element);
    // On catalog failure hydrate anyway: message ids beat a dead page.
    activateLocale(detectLanguageCode()).then(hydrate, hydrate);
  };

// used to fix ios 100vh when toolbar is shown
function computeReal100vh() {
  const doc = document.documentElement;
  let prevClientHeight: number;
  const customVar = "--vh";
  function handleResize() {
    const clientHeight = doc.clientHeight;
    if (clientHeight === prevClientHeight) return;
    requestAnimationFrame(function updateViewportHeight() {
      doc.style.setProperty(customVar, clientHeight * 0.01 + "px");
      prevClientHeight = clientHeight;
    });
  }
  handleResize();
  return handleResize;
}

// FIX (React 19 + Gatsby Slices): Gatsby renders each <Slice> (nav, footer, …)
// as a SEPARATE react-dom/server stream. If a slice contains any suspending
// boundary (React.lazy / <Suspense>), that stream emits Fizz completion scripts
// ($RC/$RT/$RV), hidden buffer <div>s and <template> placeholders. Gatsby
// stitches the slice HTML — including those trailing nodes — into the page, so
// they land as direct children of #gatsby-focus-wrapper. By the time the bundle
// runs they have already executed during HTML parse and are inert. But React
// 19's stricter hydration sees the extra <script> children of the focus-wrapper
// that its element tree doesn't contain, throws a hydration mismatch and
// regenerates the WHOLE tree — which on content-heavy pages (video, mosaics) is
// a visible disappear/reappear flash. Removing the inert artifacts before
// hydration makes the DOM match React's tree, so hydration succeeds.
const stripFizzStreamingArtifacts = (): void => {
  const fw = document.getElementById("gatsby-focus-wrapper");
  if (!fw) return;
  for (const el of Array.from(fw.children)) {
    const tag = el.tagName;
    if (
      tag === "SCRIPT" ||
      tag === "TEMPLATE" ||
      (tag === "DIV" && el.hasAttribute("hidden"))
    ) {
      el.remove();
    }
  }
};

export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  // Warm start: begin fetching the page's locale catalog right away, in
  // parallel with the app chunks, so the hydrate gate almost never waits.
  // <html lang> is only a hint here; the gate re-checks pageContext.
  activateLocale(detectLanguageCode()).catch(() => undefined);

  stripFizzStreamingArtifacts();

  assignAbTestSegmentToUser();

  // oxlint-disable-next-line typescript/no-floating-promises
  getCurrentGeolocation();

  window.addEventListener("resize", () => {
    computeReal100vh();
    setScrollOffset();
  });
  computeReal100vh();
  setScrollOffset();
};

function removeLoginLogoutParameters(location: Location) {
  const params = new URLSearchParams(location.search);
  if (params.has("login") || params.has("logout")) {
    params.delete("login");
    params.delete("logout");
    const newSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      location.pathname + (newSearch ? `?${newSearch}` : "") + location.hash
    );
  }
}

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location }) => {
  if (!location.pathname.includes("/auth/silent-login")) {
    removeLoginLogoutParameters(location);
  }
};

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: { location }
}) => {
  setScrollOffset();

  const hash = location.hash;
  const selector = hash ? hash.slice(1) : undefined;
  const validElement = selector ? document.getElementById(selector) : null;

  if (validElement) {
    setTimeout(() => {
      scrollToSection(validElement as HTMLDivElement);
    }, 500);

    return false;
  }
  return true;
};

export { wrapRootElement, wrapPageElement };
